import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, LayoutDashboard, FileText, AlertTriangle, History, LogOut, Users,
  Building, Monitor, MapPin, Store, Gift, BarChart3, Plus, Search,
  DollarSign, Briefcase, Mail, Phone, Check, X, Edit, Eye, Pause, Play,
  Wallet, Package
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

export default function AdminAdvertisers() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedAdv, setSelectedAdv] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    payment_terms: "invoice_net_30",
    content_approval_policy: "require_approval",
    notes: ""
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) fetchAdvertisers();
  }, [user, statusFilter, search]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      navigate("/admin/login");
    }
  };

  const fetchAdvertisers = async () => {
    try {
      let url = `${API}/advertisers?limit=100`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const response = await axios.get(url, { withCredentials: true });
      setAdvertisers(response.data);
    } catch (error) {
      toast.error("Failed to fetch advertisers");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/advertisers`, formData, { withCredentials: true });
      toast.success("Advertiser created successfully");
      setIsCreateOpen(false);
      setFormData({
        company_name: "", contact_name: "", contact_email: "", contact_phone: "",
        payment_terms: "invoice_net_30", content_approval_policy: "require_approval", notes: ""
      });
      fetchAdvertisers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create advertiser");
    }
  };

  const handleSuspend = async (advertiserId) => {
    try {
      await axios.post(`${API}/advertisers/${advertiserId}/suspend`, {}, { withCredentials: true });
      toast.success("Advertiser suspended");
      fetchAdvertisers();
    } catch (error) {
      toast.error("Failed to suspend advertiser");
    }
  };

  const handleReactivate = async (advertiserId) => {
    try {
      await axios.post(`${API}/advertisers/${advertiserId}/reactivate`, {}, { withCredentials: true });
      toast.success("Advertiser reactivated");
      fetchAdvertisers();
    } catch (error) {
      toast.error("Failed to reactivate advertiser");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/admin/login");
  };

  const statusColors = {
    active: "bg-emerald-500/20 text-emerald-400",
    suspended: "bg-orange-500/20 text-orange-400",
    inactive: "bg-slate-500/20 text-slate-400"
  };

  const paymentTermsLabels = {
    stripe_auto: "Stripe Auto",
    invoice_net_30: "Net 30",
    invoice_net_60: "Net 60",
    prepaid: "Prepaid"
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-advertisers">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6 overflow-y-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>ParkWise</span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" /><span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <FileText className="w-5 h-5" /><span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" /><span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" /><span>Retailers</span>
          </Link>
          
          {/* Revenue Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Revenue</p>
            <Link to="/admin/advertisers" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
              <Briefcase className="w-5 h-5" /><span>Advertisers</span>
            </Link>
            <Link to="/admin/campaigns" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="w-5 h-5" /><span>Campaigns</span>
            </Link>
            <Link to="/admin/billing" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Billing</span>
            </Link>
            <Link to="/admin/revenue" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <BarChart3 className="w-5 h-5" /><span>Revenue Dashboard</span>
            </Link>
          </div>

          {/* Commercial Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Wallet className="w-5 h-5" /><span>Commercial Dashboard</span>
            </Link>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Package className="w-5 h-5" /><span>Products & Tariffs</span>
            </Link>
          </div>

          {/* Signage CMS Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Signage CMS</p>
            <Link to="/admin/screens" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Monitor className="w-5 h-5" /><span>Screens</span>
            </Link>
            <Link to="/admin/content" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <FileText className="w-5 h-5" /><span>Content</span>
            </Link>
            <Link to="/admin/playlists" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <LayoutDashboard className="w-5 h-5" /><span>Playlists</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Advertisers</h1>
            <p className="text-slate-400">Manage advertiser accounts and billing</p>
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-advertiser-btn">
                <Plus className="w-4 h-4 mr-2" />Add Advertiser
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Advertiser</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Company Name *</Label>
                  <Input 
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                    data-testid="company-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Name *</Label>
                    <Input 
                      value={formData.contact_name}
                      onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label>Contact Email *</Label>
                    <Input 
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label>Payment Terms</Label>
                    <Select value={formData.payment_terms} onValueChange={(v) => setFormData({...formData, payment_terms: v})}>
                      <SelectTrigger className="bg-slate-800 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="stripe_auto">Stripe Auto-billing</SelectItem>
                        <SelectItem value="invoice_net_30">Invoice Net 30</SelectItem>
                        <SelectItem value="invoice_net_60">Invoice Net 60</SelectItem>
                        <SelectItem value="prepaid">Prepaid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Content Approval Policy</Label>
                  <Select value={formData.content_approval_policy} onValueChange={(v) => setFormData({...formData, content_approval_policy: v})}>
                    <SelectTrigger className="bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="require_approval">Require Approval</SelectItem>
                      <SelectItem value="auto_approve">Auto-approve</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <Button onClick={handleCreate} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-advertiser-btn">
                  Create Advertiser
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input 
              placeholder="Search advertisers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Advertisers List */}
        <div className="space-y-4">
          {advertisers.map((adv) => (
            <Card key={adv.advertiser_id} className="bg-slate-800/50 border-slate-700" data-testid={`advertiser-${adv.advertiser_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{adv.company_name}</h3>
                      <Badge className={statusColors[adv.status]}>{adv.status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-400">
                        <Users className="w-4 h-4" />
                        <span>{adv.contact_name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Mail className="w-4 h-4" />
                        <span>{adv.contact_email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <DollarSign className="w-4 h-4" />
                        <span>{paymentTermsLabels[adv.payment_terms] || adv.payment_terms}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Briefcase className="w-4 h-4" />
                        <span>Balance: £{(adv.account_balance || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {adv.status === "active" ? (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-orange-500 text-orange-400 hover:bg-orange-500/20"
                        onClick={() => handleSuspend(adv.advertiser_id)}
                      >
                        <Pause className="w-4 h-4 mr-1" />Suspend
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/20"
                        onClick={() => handleReactivate(adv.advertiser_id)}
                      >
                        <Play className="w-4 h-4 mr-1" />Activate
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {advertisers.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-400">
              <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No advertisers found</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
